import {
  MonitorHeart as IssueIcon,
  Subscriptions as RunTestIcon,
  Send as SendIcon,
  Folder as SuiteRunIcon,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Divider,
  Fade,
  IconButton,
  InputBase,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { format } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { DespleaIcon } from "../components/icons/desplega-icon";
import { useApiQuery } from "../hooks";
import { RADIUS_MD } from "../theme/theme";
import type { DiscoveryRun, Issue, TestRun, TestSuite, TestSuiteRun } from "../types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isIssue = (item: any): item is Issue => {
  return (item as Issue).issue_status !== undefined;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isTestRun = (item: any): item is TestRun => {
  return (item as TestRun).test_id !== undefined && (item as Issue).issue_status === undefined;
};

const isDiscoveryRun = (item: unknown): item is DiscoveryRun => {
  return (item as DiscoveryRun).flow_count !== undefined;
};

export default function MainPage() {
  const theme = useTheme();
  const nav = useNavigate();
  const [argusInput, setArgusInput] = useState("");

  const handleAskArgus = () => {
    const trimmed = argusInput.trim();
    if (!trimmed) {
      nav("/argus");
      return;
    }
    setArgusInput("");
    // Pass message via query param — the argus page picks it up and
    // sends it as a new session (avoids race between state clear + send).
    nav(`/argus?prompt=${encodeURIComponent(trimmed)}`);
  };

  const { data: suites } = useApiQuery<TestSuite[]>("/test-suites");

  const { data: runs } = useApiQuery<TestRun[]>("/runs?run_status=running&limit=5");
  const { data: suiteRuns } = useApiQuery<TestSuiteRun[]>("/suite-runs?run_status=running&limit=5");
  const { data: issues } = useApiQuery<Issue[]>("/issues?issue_status=open&limit=5&no_axe=true");
  const { data: discs } = useApiQuery<DiscoveryRun[]>("/discovery-runs?state=running&limit=5");

  const dataToCheck = useMemo(() => {
    return [...(runs || []), ...(suiteRuns || []), ...(issues || []), ...(discs || [])]
      .sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA;
      })
      .slice(0, 3);
  }, [runs, suiteRuns, issues, discs]);

  const getSuiteRunSuiteName = useCallback(
    (suiteRun: TestSuiteRun) => {
      return suites?.find((s) => s.id === suiteRun.test_suite_id)?.name ?? suiteRun.id;
    },
    [suites],
  );

  return (
    <Box
      data-testid="home-page"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "calc(100vh - 120px)",
        px: 3,
        py: 4,
      }}
    >
      <Fade in timeout={600}>
        <Stack spacing={5} alignItems="center" sx={{ width: "100%", maxWidth: 840 }}>
          <Typography variant="h3">What do you want to do today?</Typography>

          {/* Ask Argus input */}
          <Fade in timeout={800}>
            <Box
              sx={(t) => ({
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                maxWidth: 560,
                paddingX: 1.5,
                paddingY: 1,
                borderRadius: `${RADIUS_MD}px`,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: t.palette.background.paper,
                transition: "border-color 0.2s, box-shadow 0.2s",
                "&:focus-within": {
                  borderColor: "primary.main",
                  boxShadow: `0 0 0 1px ${t.palette.primary.main}`,
                },
                "&:hover:not(:focus-within)": {
                  borderColor: "text.disabled",
                },
              })}
            >
              <DespleaIcon sx={{ fontSize: 18, color: "text.disabled", flexShrink: 0 }} />
              <InputBase
                fullWidth
                value={argusInput}
                onChange={(e) => setArgusInput(e.target.value)}
                placeholder="Ask Argus anything..."
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  if (e.shiftKey || e.metaKey || e.ctrlKey) return;
                  if (e.nativeEvent.isComposing) return;
                  e.preventDefault();
                  void handleAskArgus();
                }}
                sx={{
                  fontSize: "15px",
                  "& .MuiInputBase-input": { padding: 0 },
                }}
              />
              <IconButton
                onClick={() => void handleAskArgus()}
                disabled={!argusInput.trim()}
                size="small"
                color="primary"
                aria-label="Send to Argus"
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Box>
          </Fade>

          <Divider sx={{ width: "100%" }} />

          <Stack spacing={2} alignItems="stretch" sx={{ width: "100%" }}>
            {/* Activity Header */}
            <div style={{ textAlign: "center", marginBottom: "2rem" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  margin: "0 auto 1rem auto",
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IssueIcon
                  sx={{
                    width: "24px",
                    height: "24px",
                    color: theme.palette.error.main,
                  }}
                />
              </div>
            </div>
            <Stack spacing={1} alignItems="center">
              {dataToCheck.length === 0 && (
                <Fade in timeout={800}>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    You have no running tests, suite runs, or open issues at the moment, you must be
                    on fire! 🔥
                  </Typography>
                </Fade>
              )}
              {dataToCheck.map((item, index) => (
                <Fade in timeout={800 + 3 * 500 + (index + 1) * 500} key={index}>
                  <Stack
                    padding={2}
                    spacing={0.5}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      border: `1px solid ${alpha(
                        item &&
                          (isIssue(item) ? theme.palette.error.main : theme.palette.info.main),
                        0.25,
                      )}`,
                      background: alpha(
                        item &&
                          (isIssue(item) ? theme.palette.error.main : theme.palette.info.main),
                        0.1,
                      ),
                      width: "100%",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: alpha(
                          item &&
                            (isIssue(item) ? theme.palette.error.main : theme.palette.info.main),
                          0.2,
                        ),
                        borderColor: alpha(
                          item &&
                            (isIssue(item) ? theme.palette.error.main : theme.palette.info.main),
                          0.5,
                        ),
                      },
                    }}
                    onClick={() => {
                      if (isIssue(item)) {
                        nav(`/issues/${item.id}`);
                      } else if (isTestRun(item)) {
                        nav(`/runs/${item.id}`);
                      } else if (isDiscoveryRun(item)) {
                        nav(`/discovery-runs/${item.id}`);
                      } else {
                        nav(`/suite-runs/${item.id}`);
                      }
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {isIssue(item) && (
                        <IssueIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
                      )}
                      {isTestRun(item) && (
                        <RunTestIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                      )}
                      {!isIssue(item) && !isTestRun(item) && (
                        <SuiteRunIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                      )}
                      <Typography
                        variant="body1"
                        sx={{
                          color: theme.palette.text.primary,
                        }}
                      >
                        {isIssue(item) && `Check the open issue "${item.title}"`}
                        {isTestRun(item) && `Watch how the test "${item.name}" is running`}
                        {isDiscoveryRun(item) &&
                          `There's a discovery run running, found ${item.discovered_flows_count} out of ${item.flow_count} user journeys so far...`}
                        {!isIssue(item) &&
                          !isTestRun(item) &&
                          !isDiscoveryRun(item) &&
                          `See the suite "${getSuiteRunSuiteName(item)}" is running...`}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {isIssue(item) && item.description}
                      {isTestRun(item) &&
                        `The run started at ${format(new Date(item.created_at), "PPpp")}`}
                      {isDiscoveryRun(item) &&
                        `The discovery started at ${format(new Date(item.created_at), "PPpp")}`}
                      {!isIssue(item) &&
                        !isTestRun(item) &&
                        !isDiscoveryRun(item) &&
                        `Suite run started at ${format(new Date(item.created_at), "PPpp")}`}
                    </Typography>
                  </Stack>
                </Fade>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Fade>
    </Box>
  );
}
